<script setup lang="ts">
import type { Component, HTMLAttributes } from 'vue';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

const props = withDefaults(defineProps<{
    title?: string;
    description?: string;
    meta?: string;
    icon?: Component | null;
    interactive?: boolean;
    loading?: boolean;
    class?: HTMLAttributes['class'];
}>(), {
    title: '',
    description: '',
    meta: '',
    icon: null,
    interactive: false,
    loading: false,
    class: '',
});

const emit = defineEmits<{
    (event: 'activate'): void;
}>();

function handleKeydown(event: KeyboardEvent) {
    if (!props.interactive || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    emit('activate');
}
</script>

<template>
    <div
        :class="cn(
            'group/entity-row flex min-w-0 items-center gap-3 border-b border-border/55 px-4 py-3 last:border-b-0',
            interactive && 'cursor-pointer transition-colors hover:bg-muted/45 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
            props.class,
        )"
        :role="interactive ? 'button' : undefined"
        :tabindex="interactive ? 0 : undefined"
        @click="interactive && emit('activate')"
        @keydown="handleKeydown"
    >
        <template v-if="loading">
            <Skeleton class="size-9 shrink-0 rounded-lg" />
            <div class="min-w-0 flex-1 space-y-2">
                <Skeleton class="h-3.5 w-2/5" />
                <Skeleton class="h-3 w-3/5" />
            </div>
        </template>
        <template v-else>
            <slot name="leading">
                <span v-if="icon" class="inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                    <component :is="icon" class="size-4" />
                </span>
            </slot>

            <div class="min-w-0 flex-1">
                <slot>
                    <p v-if="title" class="truncate text-sm font-medium text-foreground">{{ title }}</p>
                    <p v-if="description" class="mt-0.5 line-clamp-2 text-xs leading-5 text-muted-foreground">{{ description }}</p>
                </slot>
            </div>

            <div v-if="meta || $slots.trailing" class="flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
                <span v-if="meta">{{ meta }}</span>
                <slot name="trailing" />
            </div>
        </template>
    </div>
</template>
